import React, { useState, useEffect } from 'react';
import {
  Frame,
  Typography,
  Box,
  Loader,
  Button
} from 'components';
import SharedFreelancerList from './SharedFreelancerList';
import { useParams } from 'react-router-dom';
import firebase from 'utils/firebase';
import { Helmet } from 'react-helmet';
import { useContent } from 'utils';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

const SharedSearchView = ({ setSnackMessage }) => {
  const { id } = useParams();
  const [publicGroup, setPublicGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { title } = useContent();

  useEffect(() => {
    const fetchPublicGroup = async () => {
      try {
        setLoading(true);

        // Get the document from publicGroups collection
        const doc = await firebase.firestore()
          .collection('publicGroups')
          .doc(id)
          .get();

        if (!doc.exists) {
          setError('This shared search does not exist or has expired.');
          return;
        }

        const data = doc.data();

        // Check if the shared search has expired
        let expirationDate;

        try {
          // Handle different possible date formats
          if (data.expiresAt) {
            if (typeof data.expiresAt.toDate === 'function') {
              // It's a Firestore timestamp
              expirationDate = data.expiresAt.toDate();
            } else if (data.expiresAt instanceof Date) {
              // It's already a Date object
              expirationDate = data.expiresAt;
            } else if (typeof data.expiresAt === 'object' && data.expiresAt.seconds) {
              // It's a timestamp-like object with seconds
              expirationDate = new Date(data.expiresAt.seconds * 1000);
            } else if (typeof data.expiresAt === 'number') {
              // It's a timestamp number
              expirationDate = new Date(data.expiresAt);
            } else if (typeof data.expiresAt === 'string') {
              // It's a date string
              expirationDate = new Date(data.expiresAt);
            }
          }

          if (expirationDate && expirationDate < new Date()) {
            setError('This shared search has expired.');
            return;
          }
        } catch (dateError) {
          console.error('Error parsing expiration date:', dateError);
          // Continue processing even if date parsing fails
        }

        setPublicGroup(data);
      } catch (err) {
        console.error('Error fetching shared search:', err);
        setError('Error loading shared search. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPublicGroup();
  }, [id]);

  // Format expiration date if available - safely handle different date formats
  let expirationDate;

  try {
    if (publicGroup?.expiresAt) {
      if (typeof publicGroup.expiresAt.toDate === 'function') {
        // It's a Firestore timestamp
        expirationDate = publicGroup.expiresAt.toDate();
      } else if (publicGroup.expiresAt instanceof Date) {
        // It's already a Date object
        expirationDate = publicGroup.expiresAt;
      } else if (typeof publicGroup.expiresAt === 'object' && publicGroup.expiresAt.seconds) {
        // It's a timestamp-like object with seconds
        expirationDate = new Date(publicGroup.expiresAt.seconds * 1000);
      } else if (typeof publicGroup.expiresAt === 'number') {
        // It's a timestamp number
        expirationDate = new Date(publicGroup.expiresAt);
      } else if (typeof publicGroup.expiresAt === 'string') {
        // It's a date string
        expirationDate = new Date(publicGroup.expiresAt);
      }
    }
  } catch (error) {
    console.error('Error parsing date for formatting:', error);
  }

  const formattedDate = expirationDate
    ? new Date(expirationDate).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
    : '';

  return (
    <Frame maxWidth="lg">
      <Helmet>
        <title>{title} | Shared Search</title>
      </Helmet>

      {/* Back to Search button */}
      <Box mb={2}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => window.location.href = '/'}
        >
          Back to search
        </Button>
      </Box>

      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Shared Search Results
        </Typography>
        {publicGroup && (
          <Typography variant="body2" color="textSecondary">
            {publicGroup.userCount} freelancers • Expires on {formattedDate}
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <Loader />
        </Box>
      ) : error ? (
        <Box mt={4} p={3} bgcolor="error.light" borderRadius={1}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      ) : publicGroup ? (
        <>
          {/* Filter info */}
          {publicGroup.filterState && (
            <Box mb={3} p={2} bgcolor="background.paper" borderRadius={1}>
              <Typography variant="subtitle1" gutterBottom>
                Applied Filters:
              </Typography>
              {/* Show key filters if available */}
              {publicGroup.filterState.role && (
                <Typography variant="body2">
                  Role: {publicGroup.filterState.role}
                </Typography>
              )}
              {publicGroup.filterState.city && (
                <Typography variant="body2">
                  Location: {publicGroup.filterState.city}
                </Typography>
              )}
            </Box>
          )}

          {/* Freelancer list */}
          <Box mt={2}>
            <SharedFreelancerList users={publicGroup.users} />
          </Box>
        </>
      ) : null}
    </Frame>
  );
};

export default SharedSearchView;
